import React, { useEffect, useMemo, useState } from 'react';
import { adminApi, isUnauthorized } from '../../services/adminApi';
import { getAdminTheme } from './adminTheme';

type AdminTaskDto = {
  id: string;
  name: string;
  description: string;
  isEnabled: boolean;
  isRunning: boolean;
  lastStartedAtUtc?: string | null;
  lastFinishedAtUtc?: string | null;
  lastResult: string;
  lastError: string;
  lastSymbolsCount: number;
  lastBatchesCount: number;
  lastRowsUpdated: number;
};

type SchedulerResult = {
  ok: boolean;
  exitCode: number;
  stdOut: string;
  stdErr: string;
};

const formatLocal = (iso?: string | null): string => {
  if (!iso) return '';
  const raw = iso.trim();
  const hasTz = /([zZ]|[+-]\d{2}:?\d{2})$/.test(raw);
  const d = new Date(hasTz ? raw : `${raw}Z`);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem' });
};

const Indicator: React.FC<{ ok: boolean; title?: string; theme: ReturnType<typeof getAdminTheme> }> = ({
  ok,
  title,
  theme,
}) => {
  return (
    <span
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 18,
        borderRadius: 999,
        fontWeight: 900,
        color: ok ? theme.success : theme.danger,
        fontSize: 16,
        lineHeight: '18px',
      }}
    >
      {ok ? '✓' : '✗'}
    </span>
  );
};

interface AdminTasksScreenProps {
  isDarkMode: boolean;
  token: string;
  onUnauthorized?: () => void;
}

const AdminTasksScreen: React.FC<AdminTasksScreenProps> = ({ isDarkMode, token, onUnauthorized }) => {
  const theme = useMemo(() => getAdminTheme(isDarkMode), [isDarkMode]);

  const [tasks, setTasks] = useState<AdminTaskDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [intervalMinutes, setIntervalMinutes] = useState(5);
  const [taskName, setTaskName] = useState('');
  const [schedulerResult, setSchedulerResult] = useState<SchedulerResult | null>(null);

  const load = async () => {
    setError(null);
    try {
      const rows = await adminApi.listTasks(token);
      setTasks(rows);
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized?.();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed loading tasks');
    }
  };

  useEffect(() => {
    void load();
    const t = window.setInterval(() => void load(), 15000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const cardStyle: React.CSSProperties = {
    background: theme.cardBg,
    border: `1px solid ${theme.border}`,
    borderRadius: theme.radius,
    padding: 14,
  };

  const buttonBase: React.CSSProperties = {
    borderRadius: 4,
    padding: '8px 12px',
    border: `1px solid ${theme.border}`,
    fontWeight: 800,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  const runButtonStyle: React.CSSProperties = {
    ...buttonBase,
    background: theme.success,
    color: '#fff',
    border: 'none',
  };

  const editButtonStyle: React.CSSProperties = {
    ...buttonBase,
    background: theme.cardBg,
    color: theme.text,
  };

  const inputStyle: React.CSSProperties = {
    height: 34,
    borderRadius: 4,
    border: `1px solid ${theme.border}`,
    background: theme.inputBg,
    padding: '0 10px',
    color: theme.text,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    fontWeight: 700,
  };

  const [editingId, setEditingId] = useState<string | null>(null);
  const rows = useMemo(() => {
    return tasks.filter((t) => t.id !== 'quotes-refresh');
  }, [tasks]);

  const taskKind = (id: string): 'quotes-refresh' | 'quotes-cache-refresh' | 'alerts-status-refresh' | 'subscription-expiry-warning-sms' | 'other' => {
    const key = String(id || '').trim().toLowerCase();
    if (key === 'quotes-refresh') return 'quotes-refresh';
    if (key === 'quotes-cache-refresh') return 'quotes-cache-refresh';
    if (key === 'alerts-status-refresh') return 'alerts-status-refresh';
    if (key === 'subscription-expiry-warning-sms') return 'subscription-expiry-warning-sms';
    return 'other';
  };

  const taskPlaceholder = (kind: ReturnType<typeof taskKind>): string => {
    if (kind === 'quotes-cache-refresh') return 'Garp - Quotes Cache Refresh';
    if (kind === 'alerts-status-refresh') return 'Garp - Alerts Status Refresh';
    if (kind === 'subscription-expiry-warning-sms') return 'Garp - Subscription Expiry Warning SMS';
    return 'Garp - Quotes Refresh';
  };

  return (
    <div style={{ display: 'grid', gap: 12, direction: 'ltr' }}>
      <div style={cardStyle}>
        <div style={{ fontSize: 18, fontWeight: 900, color: theme.text, marginBottom: 6 }}>Scheduled Tasks</div>
        <div style={{ color: theme.subText, fontSize: 13, fontWeight: 700 }}>
          Note: a site restart may be required after updating a scheduled task.
        </div>
        <div style={{ color: theme.subText, fontSize: 12, fontWeight: 700, marginTop: 6 }}>
          Auto toggle controls in-process runners (background/opportunistic).
        </div>
      </div>

      {error && <div style={{ ...cardStyle, borderColor: theme.danger, color: theme.danger, fontWeight: 800 }}>{error}</div>}

      <div style={cardStyle}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
            <thead>
              <tr style={{ background: isDarkMode ? theme.hoverBg : '#f3f4f6' }}>
                <th style={{ padding: 10, border: `1px solid ${theme.border}`, textAlign: 'left' }}>Name</th>
                <th style={{ padding: 10, border: `1px solid ${theme.border}`, textAlign: 'center' }}>Seconds (interval)</th>
                <th style={{ padding: 10, border: `1px solid ${theme.border}`, textAlign: 'center' }}>Auto</th>
                <th style={{ padding: 10, border: `1px solid ${theme.border}`, textAlign: 'center' }}>Stop on error</th>
                <th style={{ padding: 10, border: `1px solid ${theme.border}`, textAlign: 'center' }}>Last start</th>
                <th style={{ padding: 10, border: `1px solid ${theme.border}`, textAlign: 'center' }}>Last end</th>
                <th style={{ padding: 10, border: `1px solid ${theme.border}`, textAlign: 'center' }}>Last success</th>
                <th style={{ padding: 10, border: `1px solid ${theme.border}`, textAlign: 'center' }}>Run now</th>
                <th style={{ padding: 10, border: `1px solid ${theme.border}`, textAlign: 'center' }}>Edit</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t, idx) => {
                const isEditing = editingId === t.id;
                const seconds = taskKind(t.id) === 'subscription-expiry-warning-sms' ? 86400 : intervalMinutes * 60;
                const enabled = t.isEnabled;
                const hasError = Boolean(t.lastError && t.lastError.trim().length > 0);
                const lastResultText = String(t.lastResult || '').trim();
                const lastErrorText = String(t.lastError || '').trim();
                const resultLower = lastResultText.toLowerCase();
                const isSuccessLike = !lastErrorText && (resultLower.startsWith('ok') || resultLower.startsWith('skipped'));
                const lastSuccess = isSuccessLike ? formatLocal(t.lastFinishedAtUtc) : '';

                return (
                  <React.Fragment key={t.id}>
                    <tr style={{ background: idx % 2 === 0 ? 'transparent' : isDarkMode ? theme.hoverBg : '#fafafa' }}>
                      <td style={{ padding: 10, border: `1px solid ${theme.border}`, fontWeight: 800, color: theme.text }}>
                        {t.name}
                      </td>
                      <td style={{ padding: 10, border: `1px solid ${theme.border}`, textAlign: 'center', fontWeight: 800, color: theme.text }}>
                        {seconds}
                      </td>
                      <td style={{ padding: 10, border: `1px solid ${theme.border}`, textAlign: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
                          <Indicator ok={enabled} title={enabled ? 'Auto enabled' : 'Auto disabled'} theme={theme} />
                          <button
                            style={editButtonStyle}
                            disabled={busy}
                            onClick={async () => {
                              setBusy(true);
                              setError(null);
                              try {
                                await adminApi.setTaskEnabled(token, t.id, !enabled);
                                await load();
                              } catch (e) {
                                if (isUnauthorized(e)) onUnauthorized?.();
                                else setError(e instanceof Error ? e.message : 'Failed updating auto status');
                              } finally {
                                setBusy(false);
                              }
                            }}
                          >
                            {enabled ? 'Disable' : 'Enable'}
                          </button>
                        </div>
                      </td>
                      <td style={{ padding: 10, border: `1px solid ${theme.border}`, textAlign: 'center' }}>
                        <Indicator ok={!hasError} title={hasError ? t.lastError : 'No error'} theme={theme} />
                      </td>
                      <td style={{ padding: 10, border: `1px solid ${theme.border}`, textAlign: 'center', color: theme.text, fontWeight: 700 }}>
                        {formatLocal(t.lastStartedAtUtc)}
                      </td>
                      <td style={{ padding: 10, border: `1px solid ${theme.border}`, textAlign: 'center', color: theme.text, fontWeight: 700 }}>
                        {formatLocal(t.lastFinishedAtUtc)}
                      </td>
                      <td style={{ padding: 10, border: `1px solid ${theme.border}`, textAlign: 'center', color: theme.text, fontWeight: 700 }}>
                        {lastSuccess}
                      </td>
                      <td style={{ padding: 10, border: `1px solid ${theme.border}`, textAlign: 'center' }}>
                        <button
                          style={runButtonStyle}
                          disabled={busy}
                          onClick={async () => {
                            setBusy(true);
                            setError(null);
                            setSchedulerResult(null);
                            try {
                              const kind = taskKind(t.id);
                              if (kind === 'quotes-cache-refresh') {
                                await adminApi.runQuotesCacheRefreshTask(token);
                              } else if (kind === 'alerts-status-refresh') {
                                await adminApi.runAlertsStatusRefreshTask(token);
                              } else if (kind === 'subscription-expiry-warning-sms') {
                                await adminApi.runSubscriptionExpiryWarningSmsTask(token);
                              } else {
                                await adminApi.runQuotesRefreshTask(token);
                              }
                              await load();
                            } catch (e) {
                              if (isUnauthorized(e)) onUnauthorized?.();
                              else setError(e instanceof Error ? e.message : 'Failed running task');
                            } finally {
                              setBusy(false);
                            }
                          }}
                        >
                          Run now
                        </button>
                      </td>
                      <td style={{ padding: 10, border: `1px solid ${theme.border}`, textAlign: 'center' }}>
                        <button
                          style={editButtonStyle}
                          disabled={busy}
                          onClick={() => {
                            setSchedulerResult(null);
                            setError(null);
                            setEditingId((prev) => (prev === t.id ? null : t.id));
                          }}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>

                    {isEditing ? (
                      <tr>
                        <td colSpan={9} style={{ padding: 12, border: `1px solid ${theme.border}`, background: isDarkMode ? theme.hoverBg : '#f9fafb' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px 220px', gap: 10, alignItems: 'end' }}>
                            <div>
                              <div style={{ color: theme.subText, fontSize: 12, fontWeight: 800, marginBottom: 6 }}>Task name (optional)</div>
                              <input
                                value={taskName}
                                onChange={(e) => setTaskName(e.target.value)}
                                style={inputStyle}
                                placeholder={taskPlaceholder(taskKind(t.id))}
                              />
                            </div>
                            <div>
                              <div style={{ color: theme.subText, fontSize: 12, fontWeight: 800, marginBottom: 6 }}>Minutes (frequency)</div>
                              <input
                                value={String(intervalMinutes)}
                                onChange={(e) => setIntervalMinutes(Math.max(1, Math.min(1440, Number(e.target.value) || 1)))}
                                style={inputStyle}
                                inputMode="numeric"
                              />
                            </div>
                            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-start', flexWrap: 'wrap' }}>
                              <button
                                style={{ ...runButtonStyle, background: theme.primary }}
                                disabled={busy}
                                onClick={async () => {
                                  setBusy(true);
                                  setError(null);
                                  try {
                                    const kind = taskKind(t.id);
                                    const res =
                                      kind === 'quotes-cache-refresh'
                                        ? await adminApi.upsertQuotesCacheRefreshSchedule(token, {
                                            taskName: taskName.trim() || null,
                                            intervalMinutes,
                                            enabled: true,
                                          })
                                        : kind === 'alerts-status-refresh'
                                          ? await adminApi.upsertAlertsStatusRefreshSchedule(token, {
                                              taskName: taskName.trim() || null,
                                              intervalMinutes,
                                              enabled: true,
                                            })
                                          : kind === 'subscription-expiry-warning-sms'
                                            ? await adminApi.upsertSubscriptionExpiryWarningSmsSchedule(token, {
                                                taskName: taskName.trim() || null,
                                                intervalMinutes: 1440,
                                                enabled: true,
                                              })
                                            : await adminApi.upsertQuotesRefreshSchedule(token, {
                                              taskName: taskName.trim() || null,
                                              intervalMinutes,
                                              enabled: true,
                                            });
                                    setSchedulerResult(res);
                                  } catch (e) {
                                    if (isUnauthorized(e)) onUnauthorized?.();
                                    else setError(e instanceof Error ? e.message : 'Failed scheduling task');
                                  } finally {
                                    setBusy(false);
                                  }
                                }}
                              >
                                Save & enable
                              </button>
                              <button
                                style={editButtonStyle}
                                disabled={busy}
                                onClick={async () => {
                                  setBusy(true);
                                  setError(null);
                                  try {
                                    const kind = taskKind(t.id);
                                    const res =
                                      kind === 'quotes-cache-refresh'
                                        ? await adminApi.upsertQuotesCacheRefreshSchedule(token, {
                                            taskName: taskName.trim() || null,
                                            intervalMinutes,
                                            enabled: false,
                                          })
                                        : kind === 'alerts-status-refresh'
                                          ? await adminApi.upsertAlertsStatusRefreshSchedule(token, {
                                              taskName: taskName.trim() || null,
                                              intervalMinutes,
                                              enabled: false,
                                            })
                                          : kind === 'subscription-expiry-warning-sms'
                                            ? await adminApi.upsertSubscriptionExpiryWarningSmsSchedule(token, {
                                                taskName: taskName.trim() || null,
                                                intervalMinutes: 1440,
                                                enabled: false,
                                              })
                                            : await adminApi.upsertQuotesRefreshSchedule(token, {
                                              taskName: taskName.trim() || null,
                                              intervalMinutes,
                                              enabled: false,
                                            });
                                    setSchedulerResult(res);
                                  } catch (e) {
                                    if (isUnauthorized(e)) onUnauthorized?.();
                                    else setError(e instanceof Error ? e.message : 'Failed disabling schedule');
                                  } finally {
                                    setBusy(false);
                                  }
                                }}
                              >
                                Disable
                              </button>
                              <button
                                style={editButtonStyle}
                                disabled={busy}
                                onClick={async () => {
                                  setBusy(true);
                                  setError(null);
                                  try {
                                    const kind = taskKind(t.id);
                                    if (kind === 'quotes-cache-refresh') await adminApi.stopQuotesCacheRefreshTask(token);
                                    else if (kind === 'alerts-status-refresh') await adminApi.stopAlertsStatusRefreshTask(token);
                                    else if (kind === 'subscription-expiry-warning-sms') await adminApi.stopSubscriptionExpiryWarningSmsTask(token);
                                    else await adminApi.stopQuotesRefreshTask(token);
                                    await load();
                                  } catch (e) {
                                    if (isUnauthorized(e)) onUnauthorized?.();
                                    else setError(e instanceof Error ? e.message : 'Failed stopping task');
                                  } finally {
                                    setBusy(false);
                                  }
                                }}
                              >
                                Stop run
                              </button>
                            </div>
                          </div>

                          {schedulerResult ? (
                            <div style={{ marginTop: 10 }}>
                              <div style={{ fontWeight: 900, color: schedulerResult.ok ? theme.success : theme.danger, marginBottom: 6 }}>
                                Task Scheduler: {schedulerResult.ok ? 'OK' : 'Failed'} (exit {schedulerResult.exitCode})
                              </div>
                              {schedulerResult.stdErr ? (
                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', color: theme.danger, fontSize: 12 }}>{schedulerResult.stdErr}</pre>
                              ) : null}
                              {schedulerResult.stdOut ? (
                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', color: theme.subText, fontSize: 12 }}>{schedulerResult.stdOut}</pre>
                              ) : null}
                            </div>
                          ) : null}

                          {(t.lastResult || t.lastError) && (
                            <div style={{ marginTop: 10 }}>
                              {t.lastResult ? (
                                <div style={{ fontWeight: 800, color: theme.text, marginBottom: t.lastError ? 6 : 0 }}>Last result: {t.lastResult}</div>
                              ) : null}
                              {t.lastError ? (
                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', color: theme.danger, fontSize: 12 }}>{t.lastError}</pre>
                              ) : null}
                            </div>
                          )}
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminTasksScreen;
